import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * LR HUB "Trending Tags" rail box.
 *
 * Data source: `hub_posts.tags` ONLY, aggregated server-side by the
 * `hub_trending_tags(_days, _limit)` RPC (approved, non-deleted posts).
 *
 * ISOLATION: no load board / freight / billing tables are read.
 */

export interface HubTrendingTag {
  tag: string;
  /** posts using the tag in the current window */
  activity: number;
  /** posts using the tag in the immediately-prior window */
  priorActivity: number;
  /** "up" | "down" | "flat" | "new" (no prior data) */
  trend: "up" | "down" | "flat" | "new";
  /** e.g. "18%", or "New" when there is no prior-window data */
  label: string;
}

export function useHubTrendingTags(days = 7, limit = 5) {
  return useQuery({
    queryKey: ["hub-trending-tags", days, limit],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<HubTrendingTag[]> => {
      const { data, error } = await supabase.rpc("hub_trending_tags", {
        _days: days,
        _limit: limit,
      });
      if (error) throw error;
      return (data ?? []).map((r: { tag: string; activity: number; prior_activity: number }) => {
        const activity = Number(r.activity ?? 0);
        const priorActivity = Number(r.prior_activity ?? 0);
        if (priorActivity === 0) {
          return { tag: r.tag, activity, priorActivity, trend: "new" as const, label: "New" };
        }
        const pct = Math.round(((activity - priorActivity) / priorActivity) * 100);
        return {
          tag: r.tag,
          activity,
          priorActivity,
          trend: pct > 0 ? ("up" as const) : pct < 0 ? ("down" as const) : ("flat" as const),
          label: `${Math.abs(pct)}%`,
        };
      });
    },
  });
}

export default useHubTrendingTags;
